import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Compass, ChevronDown, ChevronRight,
  Zap, Loader2, RefreshCw, Plus, Check, Layers, Sunrise, Play
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import PatternIndex from '../components/PatternIndex';

export const Navigator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const incomingSignal = location.state?.signal || null;
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [navigatorData, setNavigatorData] = useState(null);
  const [history, setHistory] = useState([]);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [showNextLayers, setShowNextLayers] = useState(false);
  const [addedMissions, setAddedMissions] = useState(new Set());
  const [todayMorning, setTodayMorning] = useState(null);
  const [todayMorningLoading, setTodayMorningLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchNavigatorData();
    fetchTodayMorning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTodayMorning = async () => {
    setTodayMorningLoading(true);
    try {
      const response = await api.get('/checkin/morning/today');
      setTodayMorning(response.data);
    } catch (error) {
      setTodayMorning(null);
    } finally {
      setTodayMorningLoading(false);
    }
  };

  const fetchNavigatorData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/navigator/current');
      setNavigatorData(response.data.current);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch navigator:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateWeeklyInsight = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/navigator/generate-weekly');
      setNavigatorData(response.data);
      toast.success(language === 'tr' ? 'Navigator güncellendi' : 'Navigator updated');
      fetchNavigatorData();
    } catch (error) {
      console.error('Failed to generate:', error);
      toast.error(language === 'tr' ? 'Güncellenemedi' : 'Failed to update');
    } finally {
      setGenerating(false);
    }
  };

  const addToMissions = async (text, frequency = 'weekly') => {
    try {
      await api.post('/missions', {
        text,
        domain: 'navigator',
        frequency,
        source: 'navigator'
      });
      setAddedMissions(prev => new Set([...prev, text]));
      toast.success(language === 'tr' ? 'Misyona eklendi' : 'Added to missions');
    } catch (error) {
      toast.error(language === 'tr' ? 'Eklenemedi' : 'Failed to add');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Compass className="w-12 h-12 text-[#D4AF37] animate-pulse" />
      </div>
    );
  }

  const data = navigatorData;
  const hasCutDirective = data?.cut && data?.directive;

  const stopwords = new Set([
    // EN
    'the','a','an','and','or','but','if','then','for','to','of','in','on','at','by','with','from','is','are','was','be','been','being','this','that','these','those','your','my','our','their','his','her','i','we','you','they','me','us','them','it','its','will','shall','can','could','should','would','may','might','do','does','did','done','doing','have','has','had','not','no','yes','as','so','than','too','very','just','only','up','down','out','over','under','into','about','any','all','each','every','some','one','two','three','day','week','today',
    // TR
    've','veya','ya','da','de','ile','için','bir','bu','şu','o','ben','sen','biz','siz','onlar','benim','senin','bizim','sizin','onun','bugün','hafta','gün','çok','az','her','ama','fakat','değil','mi','mı','mu','mü','ne','nasıl','nerede','olmak','var','yok','ise','gibi','kadar','sonra','önce','yine','hala','artık','ancak','çünkü','eğer'
  ]);
  const tokenize = (s) =>
    (s || '').toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter(w => w.length >= 3 && !stopwords.has(w));

  // Detect if Today's One Win aligns with this week's Directive (via keyword overlap)
  const computeAlignment = () => {
    const oneWin = todayMorning?.one_win;
    if (!oneWin || !data?.directive) return false;
    const winTokens = new Set(tokenize(oneWin));
    if (winTokens.size === 0) return false;
    const weeklyText = [data.directive, ...(data.next_layers || []), ...(data.habits || [])].join(' ');
    const weeklyTokens = new Set(tokenize(weeklyText));
    let overlap = 0;
    winTokens.forEach(w => {
      if (weeklyTokens.has(w)) overlap += 1;
    });
    return overlap >= 2 || (overlap >= 1 && overlap / winTokens.size >= 0.5);
  };

  const isAligned = computeAlignment();

  // Compute the set of words from the incoming signal that also appear in the
  // weekly directive — they get bolded + gold inside the directive text for
  // a felt sense of continuity from tap → page.
  const signalSharedTokens = (() => {
    if (!incomingSignal?.message || !data?.directive) return new Set();
    const signalTokens = new Set(tokenize(incomingSignal.message));
    const directiveTokens = new Set(tokenize(data.directive));
    const shared = new Set();
    signalTokens.forEach(t => { if (directiveTokens.has(t)) shared.add(t); });
    return shared;
  })();

  // Render a string with the given shared-token set highlighted.
  // `animateShared` staggers a gentle scale/opacity pulse on each highlighted word.
  const renderWithHighlight = (text, sharedSet, animateShared = false) => {
    if (!text || !sharedSet || sharedSet.size === 0) return text;
    const parts = text.split(/(\s+)/);
    let sharedIdx = 0;
    return parts.map((p, i) => {
      const clean = p.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
      if (clean && sharedSet.has(clean)) {
        if (animateShared) {
          const delay = 0.35 + sharedIdx * 0.18;
          sharedIdx += 1;
          return (
            <motion.span
              key={i}
              initial={{ opacity: 0.55, scale: 1, color: '#F8F5F2' }}
              animate={{
                opacity: [0.55, 1, 0.92, 1],
                scale: [1, 1.06, 1, 1],
                color: ['#F8F5F2', '#D4AF37', '#D4AF37', '#D4AF37'],
              }}
              transition={{ duration: 1.1, delay, ease: 'easeOut' }}
              className="font-medium inline-block"
            >
              {p}
            </motion.span>
          );
        }
        return (
          <span key={i} className="text-[#D4AF37] font-medium">{p}</span>
        );
      }
      return <React.Fragment key={i}>{p}</React.Fragment>;
    });
  };

  return (
    <div className="min-h-screen relative py-12 px-6" style={{ position: 'relative', zIndex: 1 }}>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-white/50 hover:text-white transition-colors flex items-center gap-2 text-sm font-sans uppercase tracking-widest"
            data-testid="navigator-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'tr' ? 'Panel' : 'Dashboard'}
          </button>
          <button
            onClick={generateWeeklyInsight}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all text-sm font-sans disabled:opacity-50"
            data-testid="navigator-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            {generating ? (language === 'tr' ? 'Güncelleniyor...' : 'Updating...') : (language === 'tr' ? 'Güncelle' : 'Refresh')}
          </button>
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <Compass className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
          <h1 className="font-serif text-3xl text-[#F8F5F2]">Navigator</h1>
          {/* Signal echo — if user tapped a signal to land here, show it as the opening line */}
          {incomingSignal?.message && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`mt-3 font-serif text-base sm:text-lg italic px-4 ${
                signalSharedTokens.size > 0 ? 'text-[#F8F5F2]/80' : 'text-[#D4AF37]/90'
              }`}
              data-testid="navigator-signal-echo"
            >
              {renderWithHighlight(incomingSignal.message, signalSharedTokens)}
            </motion.p>
          )}
          {data?.archetypes?.length > 0 && (
            <div className="flex justify-center gap-2 mt-3">
              {data.archetypes.map((a, i) => (
                <span key={i} className="text-xs px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] font-sans">
                  {a}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* TODAY — Morning Summary (mirrors Dashboard Today card) */}
        <div className="mb-8" data-testid="navigator-today-panel">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] font-sans mb-2 text-center">
            {language === 'tr' ? 'Bugün' : 'Today'}
          </p>
          {todayMorningLoading ? (
            <div className="glass-panel rounded-2xl p-6 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
            </div>
          ) : todayMorning?.submitted ? (
            <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-b from-[#D4AF37]/5 to-transparent overflow-hidden">
              {/* Daily State */}
              <div className="px-5 pt-5 pb-3">
                {todayMorning.output?.reflection && (
                  <p className="text-[#A1A1AA] font-sans leading-relaxed text-sm" data-testid="nav-today-state">
                    {todayMorning.output.reflection}
                  </p>
                )}
                <div className="flex items-center flex-wrap gap-2 mt-3">
                  {todayMorning.feeling && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-sans capitalize ${
                      todayMorning.feeling === 'calm' ? 'bg-blue-500/15 text-blue-300' :
                      todayMorning.feeling === 'focused' ? 'bg-emerald-500/15 text-emerald-300' :
                      todayMorning.feeling === 'scattered' ? 'bg-amber-500/15 text-amber-300' :
                      todayMorning.feeling === 'heavy' ? 'bg-purple-500/15 text-purple-300' :
                      'bg-rose-500/15 text-rose-300'
                    }`}>{todayMorning.feeling}</span>
                  )}
                  {todayMorning.sleep_quality && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-[#A1A1AA] font-sans">
                      {language === 'tr' ? 'Uyku' : 'Sleep'}: {todayMorning.sleep_quality}/10
                    </span>
                  )}
                  {todayMorning.output?.daily_tone?.name && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] font-sans">
                      {todayMorning.output.daily_tone.name}
                    </span>
                  )}
                </div>
              </div>
              {/* Today's Direction — One Win */}
              {todayMorning.one_win && (
                <div
                  className={`relative mx-5 mb-4 p-4 rounded-xl transition-all ${
                    todayMorning.direction_achieved
                      ? 'bg-emerald-500/10 border border-emerald-500/40'
                      : isAligned
                        ? 'bg-[#D4AF37]/15 border border-[#D4AF37]/60 shadow-[0_0_20px_rgba(212,175,55,0.25)]'
                        : 'bg-[#D4AF37]/10 border border-[#D4AF37]/30'
                  }`}
                  data-testid="nav-today-direction"
                >
                  {isAligned && !todayMorning.direction_achieved && (
                    <div
                      className="absolute -top-2.5 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#030305] text-[10px] font-sans font-semibold uppercase tracking-wider shadow-lg"
                      data-testid="alignment-badge"
                      title={language === 'tr' ? 'Haftalık yönelimle hizalı' : "Aligned with this week's directive"}
                    >
                      <Zap className="w-3 h-3" />
                      {language === 'tr' ? 'Hizalı' : 'Aligned'}
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/70 font-sans mb-2">
                        {language === 'tr' ? 'Bugünün Yönü' : "Today's Direction"}
                      </p>
                      <p className={`font-serif text-xl leading-snug ${
                        todayMorning.direction_achieved ? 'text-emerald-300 line-through decoration-emerald-400/40' : 'text-[#F8F5F2]'
                      }`}>
                        {todayMorning.one_win}
                      </p>
                      {isAligned && !todayMorning.direction_achieved && (
                        <p className="text-[11px] text-[#D4AF37]/80 font-sans italic mt-2 leading-snug">
                          {language === 'tr'
                            ? 'Bu hareket, bu haftaki yönelimini ileriye taşıyor.'
                            : "This move carries this week's directive forward."}
                        </p>
                      )}
                      {todayMorning.direction_achieved && (
                        <p className="text-[11px] text-emerald-300/80 font-sans mt-2">
                          {language === 'tr' ? 'Tamamlandı. Güzel.' : 'Marked complete. Well done.'}
                        </p>
                      )}
                    </div>
                    {/* Check / toggle */}
                    <button
                      onClick={async () => {
                        const newVal = !todayMorning.direction_achieved;
                        setTodayMorning(prev => prev ? { ...prev, direction_achieved: newVal } : prev);
                        if (newVal) window.dispatchEvent(new Event('signal:celebrate'));
                        try {
                          await api.post('/checkin/direction/mark', { achieved: newVal });
                          toast.success(newVal
                            ? (language === 'tr' ? 'Yön tamamlandı' : 'Direction complete')
                            : (language === 'tr' ? 'Geri alındı' : 'Unmarked'));
                        } catch (error) {
                          setTodayMorning(prev => prev ? { ...prev, direction_achieved: !newVal } : prev);
                          toast.error(language === 'tr' ? 'Kaydedilemedi' : 'Failed to save');
                        }
                      }}
                      className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        todayMorning.direction_achieved
                          ? 'bg-emerald-500/30 border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/40'
                          : 'bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/20'
                      }`}
                      title={todayMorning.direction_achieved
                        ? (language === 'tr' ? 'Tamamlandı — geri al' : 'Completed — undo')
                        : (language === 'tr' ? 'Tamamlandı olarak işaretle' : 'Mark complete')}
                      data-testid="nav-direction-check"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
              {/* Micro Action */}
              {todayMorning.output?.micro_action && (
                <div className="px-5 pb-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/70 font-sans mb-1">
                    {language === 'tr' ? 'Sonraki Adım' : 'Next Action'}
                  </p>
                  <p className="text-[#D1D1D6] font-sans text-sm">
                    {todayMorning.output.micro_action}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate('/morning')}
              className="w-full rounded-2xl border border-dashed border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 cursor-pointer hover:border-[#D4AF37]/50 transition-all group text-left"
              data-testid="nav-morning-cta"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Sunrise className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#F8F5F2]">
                      {language === 'tr' ? 'Günü Başlat' : 'Start Your Day'}
                    </h3>
                    <p className="text-xs text-[#6B7280] font-sans">
                      {language === 'tr' ? 'Sabah kalibrasyonunuz bugünün yönünü belirler' : "Your morning calibration sets today's direction"}
                    </p>
                  </div>
                </div>
                <Play className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          )}
        </div>

        {/* Weekly divider */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] font-sans">
            {language === 'tr' ? 'Bu Hafta' : 'This Week'}
          </p>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />
        </div>

        {data ? (
          <>
            {/* CUT → DIRECT → REINFORCE */}
            {hasCutDirective ? (
              <>
                {/* CUT */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4"
                  data-testid="navigator-cut"
                >
                  <p className="text-[#A1A1AA] font-sans text-sm leading-relaxed">
                    {data.cut}
                  </p>
                </motion.div>

                {/* DIRECTIVE — The Anchor */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className={`rounded-2xl p-5 mb-4 border transition-all ${
                    signalSharedTokens.size > 0
                      ? 'bg-[#D4AF37]/15 border-[#D4AF37]/50 shadow-[0_0_20px_rgba(212,175,55,0.18)]'
                      : 'bg-[#D4AF37]/10 border-[#D4AF37]/30'
                  }`}
                  data-testid="navigator-directive"
                >
                  <p className="text-[#F8F5F2] font-serif text-xl leading-relaxed">
                    {renderWithHighlight(data.directive, signalSharedTokens, !!incomingSignal?.message)}
                  </p>
                  {signalSharedTokens.size > 0 && (
                    <p
                      className="text-[11px] text-[#D4AF37]/70 font-sans italic mt-2 leading-snug"
                      data-testid="directive-continuity-note"
                    >
                      {language === 'tr'
                        ? 'Sinyalin ve bu haftaki yönelimin aynı dilde konuşuyor.'
                        : 'Your signal and this week\'s directive are speaking the same language.'}
                    </p>
                  )}
                </motion.div>

                {/* REINFORCE */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-6 text-center"
                  data-testid="navigator-reinforce"
                >
                  <p className="text-[#D4AF37] font-serif italic">
                    {data.reinforce}
                  </p>
                </motion.div>

                {/* NEXT LAYERS (collapsible) */}
                {data.next_layers?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-6"
                  >
                    <button
                      onClick={() => setShowNextLayers(!showNextLayers)}
                      className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all"
                      data-testid="next-layers-toggle"
                    >
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-[#6B7280]" />
                        <span className="text-sm text-[#A1A1AA] font-sans">
                          {language === 'tr' ? 'Sonraki Katmanlar' : 'Once this is stable, the next layer is...'}
                        </span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-[#6B7280] transition-transform ${showNextLayers ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {showNextLayers && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-2 mt-3">
                            {data.next_layers.map((layer, idx) => (
                              <div key={idx} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-white/5">
                                <div className="flex items-start gap-2 flex-1">
                                  <span className="text-[#D4AF37] mt-0.5">&#8226;</span>
                                  <span className="text-[#D1D1D6] font-sans text-sm">{layer}</span>
                                </div>
                                <AddMissionBtn
                                  text={layer}
                                  added={addedMissions.has(layer)}
                                  onAdd={() => addToMissions(layer, 'weekly')}
                                  language={language}
                                />
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </>
            ) : (
              /* LEGACY FORMAT — core_focus / priorities */
              <>
                {data.core_focus && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl p-5 mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30"
                  >
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/70 font-sans mb-2">
                      {language === 'tr' ? 'Odak' : 'Core Focus'}
                    </p>
                    <p className="text-[#F8F5F2] font-serif text-xl leading-relaxed">{data.core_focus}</p>
                  </motion.div>
                )}
                {data.priorities?.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
                    <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans mb-3">
                      {language === 'tr' ? 'Öncelikler' : 'Priorities'}
                    </p>
                    <div className="space-y-2">
                      {data.priorities.map((p, idx) => (
                        <div key={idx} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                          <div className="flex items-start gap-2 flex-1">
                            <span className="text-[#D4AF37] mt-0.5">&#8226;</span>
                            <span className="text-[#D1D1D6] font-sans text-sm">{p}</span>
                          </div>
                          <AddMissionBtn text={p} added={addedMissions.has(p)} onAdd={() => addToMissions(p, 'weekly')} language={language} />
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </>
            )}

            {/* HABITS with Add to Missions */}
            {data.habits?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-6"
              >
                <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans mb-3">
                  {language === 'tr' ? 'Günlük Alışkanlıklar' : 'Daily Habits'}
                </p>
                <div className="space-y-2">
                  {data.habits.map((habit, idx) => (
                    <div key={idx} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-start gap-2 flex-1">
                        <Zap className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span className="text-[#D1D1D6] font-sans text-sm">{habit}</span>
                      </div>
                      <AddMissionBtn text={habit} added={addedMissions.has(habit)} onAdd={() => addToMissions(habit, 'daily')} language={language} />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Evolution Note */}
            {data.evolution_note && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="rounded-2xl p-4 mb-6 bg-purple-500/5 border border-purple-500/20"
              >
                <p className="text-xs uppercase tracking-wider text-purple-400 font-sans mb-2">
                  {language === 'tr' ? 'Bu Hafta Ne Değişti' : 'What Shifted'}
                </p>
                <p className="text-[#D1D1D6] font-sans text-sm italic">{data.evolution_note}</p>
              </motion.div>
            )}
          </>
        ) : (
          /* No data — prompt to generate */
          <div className="text-center py-12">
            <p className="text-[#6B7280] font-sans mb-4">
              {language === 'tr' ? 'Henüz Navigator verisi yok' : 'No Navigator data yet'}
            </p>
            <button
              onClick={generateWeeklyInsight}
              disabled={generating}
              className="px-6 py-3 rounded-2xl bg-[#D4AF37] text-[#030305] font-sans text-sm uppercase tracking-widest hover:bg-[#FFBF00] transition-all disabled:opacity-50"
              data-testid="generate-first"
            >
              {generating ? (language === 'tr' ? 'Oluşturuluyor...' : 'Generating...') : (language === 'tr' ? 'Navigator Oluştur' : 'Generate Navigator')}
            </button>
          </div>
        )}

        {/* Pattern Index — 4-week longitudinal patterns */}
        <PatternIndex language={language} />

        {/* Journey History */}
        {history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <p className="text-xs uppercase tracking-wider text-[#6B7280] font-sans mb-3">
              {language === 'tr' ? 'Geçmiş' : 'Journey History'}
            </p>
            <div className="space-y-2">
              {history.map((week, idx) => (
                <div key={idx} className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
                  <button
                    onClick={() => setExpandedWeek(expandedWeek === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  >
                    <span className="text-sm text-[#A1A1AA] font-sans">
                      {new Date(week.week_start).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-[#6B7280] transition-transform ${expandedWeek === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedWeek === idx && (
                    <div className="px-3 pb-3 space-y-2">
                      {week.cut && <p className="text-[#A1A1AA] text-xs font-sans">{week.cut}</p>}
                      {(week.directive || week.core_focus) && (
                        <p className="text-[#F8F5F2] text-sm font-serif italic">{week.directive || week.core_focus}</p>
                      )}
                      {week.reinforce && <p className="text-[#D4AF37] text-xs font-serif italic">{week.reinforce}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Small "Add to Missions" button component
const AddMissionBtn = ({ text, added, onAdd, language }) => (
  <button
    onClick={(e) => { e.stopPropagation(); if (!added) onAdd(); }}
    disabled={added}
    className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-sans uppercase tracking-wider transition-all ${
      added
        ? 'bg-emerald-500/20 text-emerald-400 cursor-default'
        : 'bg-white/5 text-[#6B7280] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10'
    }`}
    data-testid={`add-mission-${text.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
  >
    {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
    {added ? (language === 'tr' ? 'Eklendi' : 'Added') : (language === 'tr' ? 'Misyon' : 'Mission')}
  </button>
);

export default Navigator;
